import type { MouseEvent } from 'react'
import { Box } from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import TaActionButton from '../TaActionButton'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { ICON_SM } from '../../common/theme'
import type { Question } from '../../types'
import { executeGenerateBatch } from '../../services/QuestionActions'
import { hasAnswerText } from '../../common/common'

interface Props {
  question: Question
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  testId?: string
}

// Row-sized variant of GenerateResponseButton. Used in
// BidQuestionsLeftPanel where the action lives next to a question row
// at a smaller 18px icon size. Click fires `executeGenerateBatch` for
// just this question (always immediate - the row context already has
// project-level batch entry points elsewhere). Icon swaps:
//   • spinner overlay when the answer is mid-generation
//   • CheckCircle when an answer already exists (and the button locks)
//   • Autorenew otherwise
export default function GenerateResponseMiniDot({
  question, onMouseEnter, onMouseLeave, testId,
}: Props) {
  const { updateQuestion, getCurrentProject, getCurrentStyleName } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)
  const queued = processing.isqueuedanswer
  const isrunning = processing.isrunninganswer
  // Once an answer exists, the button reports done - re-runs go through
  // the bigger batch modal so this row stays a single-action cell.
  const disabled = hasAnswer || processing.isprocessinganswer

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    await executeGenerateBatch(
      [question],
      project,
      getCurrentStyleName(),
      updateQuestion,
    )
  }

  return (
    <TaActionButton
      title={queued ? 'Queued' : isrunning ? 'Generating…' : hasAnswer ? 'Answer ready' : 'Generate Answer'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      running={isrunning}
      queued={queued}
      iconSize={18}
      icon={
        <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasAnswer
            ? <CheckCircleOutlineIcon sx={{ fontSize: ICON_SM, color: 'success.main' }} />
            : <AutorenewIcon sx={{ fontSize: ICON_SM }} />}
        </Box>
      }
      sx={{ p: 0.25, flexShrink: 0 }}
      testId={testId}
    />
  )
}
